/**
 * Format an arbitrary feature-property value for display in a flat
 * single-line cell or input.
 *
 * Background: feature properties live in JSONB on the server and can be any
 * JSON-compatible shape. The default `String(v)` coercion turns nested
 * objects / arrays into the literal string "[object Object]" — useless in
 * the AttributeTable, in the FeatureProperties drawer, and anywhere else
 * we surface raw values.
 *
 * Stockpile features in particular have several nested-object properties
 * (`mass`, `result`, `basePlane`, `material`) that this function unwraps
 * into pretty-printed JSON so the user can at least read what's there.
 *
 * Strings pass through untouched. Numbers / booleans use `String(v)`. `null`
 * and `undefined` render as the empty string. Anything else is JSON-encoded;
 * an encode failure (cyclic refs etc.) falls back to `[object]` so the cell
 * is never literally `[object Object]`.
 */
export function formatPropertyValue(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      // JSON.stringify throws on cycles. Should never happen for properties
      // we read off the server (the JSONB column round-trips through JSON
      // already), but the catch keeps the UI well-defined either way.
      return '[object]';
    }
  }
  return String(value);
}
